import { useEffect, useState } from 'react';
import { ApiError } from '../api';
import { addonsApi, refreshAddons, SOURCE_ORDERS, useAddons, type Addon, type ManifestInfo, type StoreEntry } from '../addons';
import { savePrefs, usePrefs } from '../prefs';

const errText = (err: unknown, fallback: string) => (err instanceof ApiError && err.message !== err.code ? err.message : fallback);

/** Waar komt het afspelen vandaan: Plex, addons of allebei (per profiel). */
export function SourceOrderSetting() {
  const prefs = usePrefs();
  const addons = useAddons();
  if (addons && !addons.allowed) return null;
  return (
    <section className="settings-block" aria-label="Volgorde van bronnen">
      <h2>Volgorde van bronnen</h2>
      <p className="subtle hint">Welke bron tumedia als eerste kiest bij afspelen. Alleen voor dit profiel.</p>
      <div className="ad-orders" role="radiogroup" aria-label="Volgorde van bronnen">
        {SOURCE_ORDERS.map(([id, label, hint]) => (
          <button key={id} className="ad-order" role="radio" aria-checked={prefs.sourceOrder === id} onClick={() => void savePrefs({ sourceOrder: id }).then(refreshAddons)}>
            <strong>{label}</strong>
            <span className="subtle">{hint}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

const PROFILES: ['best' | 'balanced' | 'saver', string, string][] = [
  ['best', 'Beste kwaliteit', '4K, HDR en Atmos als het kan'],
  ['balanced', 'In balans', '1080p, snel starten'],
  ['saver', 'Zuinig', '720p, kleine bestanden'],
];

/** Slimme bronkeuze bij addons: kwaliteitsprofiel en wat er automatisch gebeurt. */
export function SmartSourceSettings() {
  const prefs = usePrefs();
  const addons = useAddons();
  if (addons && !addons.allowed) return null;
  const sw = (key: 'autoPick' | 'failover' | 'prefetchNext' | 'trashScores' | 'failoverSameQuality' | 'failoverCachedOnly', label: string, hint: string) => (
    <div className="ua2-row">
      <span className="ua2-row-text">
        <label htmlFor={`pick-${key}`}>{label}</label>
        <span className="subtle">{hint}</span>
      </span>
      <input id={`pick-${key}`} className="sw" type="checkbox" role="switch" checked={prefs[key]} onChange={(e) => void savePrefs({ [key]: e.target.checked } as Partial<typeof prefs>)} />
    </div>
  );
  return (
    <>
      <section className="settings-block" aria-label="Kwaliteitsprofiel">
        <h2>Kwaliteitsprofiel</h2>
        <p className="subtle hint">Hiermee kiest tumedia de beste bron uit je addons. Bronnen die bij je debrid-dienst klaarstaan gaan altijd voor.</p>
        <div className="ad-orders" role="radiogroup" aria-label="Kwaliteitsprofiel">
          {PROFILES.map(([id, label, hint]) => (
            <button key={id} className="ad-order" role="radio" aria-checked={prefs.qualityProfile === id} onClick={() => void savePrefs({ qualityProfile: id })}>
              <strong>{label}</strong>
              <span className="subtle">{hint}</span>
            </button>
          ))}
        </div>
        <div className="ua2-selects" style={{ marginTop: 12 }}>
          <label className="ua2-select">Beeld
            <select value={prefs.hdrPref} onChange={(e) => void savePrefs({ hdrPref: e.target.value as 'any' | 'sdr' })}>
              <option value="any">Dolby Vision en HDR toegestaan</option>
              <option value="sdr">Liefst zonder HDR (SDR)</option>
            </select>
          </label>
          <label className="ua2-select">Nederlands
            <select value={prefs.dutchPref} onChange={(e) => void savePrefs({ dutchPref: e.target.value as 'off' | 'subs' | 'audio' })}>
              <option value="off">Geen voorkeur</option>
              <option value="subs">Liefst met Nederlandse ondertitels</option>
              <option value="audio">Liefst Nederlands gesproken</option>
            </select>
          </label>
          <label className="ua2-select">Bronnen per kwaliteit
            <select value={String(prefs.sourcesPerQuality)} onChange={(e) => void savePrefs({ sourcesPerQuality: Number(e.target.value) })}>
              <option value="2">2 per kwaliteit</option>
              <option value="3">3 per kwaliteit</option>
              <option value="5">5 per kwaliteit</option>
              <option value="0">Alles tonen</option>
            </select>
          </label>
          <NeverList />
          <label className="ua2-select">Grootste bestand
            <select value={String(prefs.maxSizeGb)} onChange={(e) => void savePrefs({ maxSizeGb: Number(e.target.value) })}>
              <option value="0">Geen grens</option>
              {[60, 30, 15, 8, 4].map((g) => <option key={g} value={g}>{g} GB</option>)}
            </select>
          </label>
        </div>
      </section>
      <section className="settings-block" aria-label="Automatisch">
        <h2>Automatisch</h2>
        {sw('autoPick', 'Meteen de beste bron aanbieden', 'Eén knop Afspelen met de beste bron; de lijst staat eronder als je zelf wilt kiezen')}
        {sw('failover', 'Bij een fout de volgende bron proberen', 'Start een bron niet of valt hij weg, dan gaat tumedia door naar de volgende')}
        {prefs.failover && (
          <div className="fo-rules">
            <div className="form-grid">
              <label className="ua2-select">Hoeveel bronnen proberen
                <select value={String(prefs.failoverMax ?? 5)} onChange={(e) => void savePrefs({ failoverMax: Number(e.target.value) })}>
                  {[2, 3, 5, 8].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label className="ua2-select">Overstappen als het beeld blijft hangen na
                <select value={String(prefs.failoverStall ?? 25)} onChange={(e) => void savePrefs({ failoverStall: Number(e.target.value) })}>
                  {[10, 15, 25, 40].map((n) => <option key={n} value={n}>{n} seconden</option>)}
                </select>
              </label>
            </div>
            {sw('failoverSameQuality', 'Eerst dezelfde kwaliteit', 'Viel een 4K-bron weg, dan eerst een andere 4K-bron; pas als die er niet zijn een lagere')}
            {sw('failoverCachedOnly', 'Alleen bronnen die klaarstaan', 'Sla bronnen over die bij debrid nog moeten downloaden (die starten niet meteen)')}
          </div>
        )}
        {sw('trashScores', 'Releases beoordelen met TRaSH Guides', 'Goede release-groepen (Remux, Bluray, WEB) omhoog, slechte releases (upscales, 3D, LQ) omlaag: zoals Radarr en Sonarr dat doen')}
        {sw('prefetchNext', 'Bronnen alvast klaarzetten', 'Op de detailpagina en tegen het einde van een aflevering worden bronnen al gezocht, zodat afspelen meteen start')}
      </section>
    </>
  );
}

function Caps({ a }: { a: Pick<Addon, 'kind' | 'resources' | 'catalogs'> }) {
  const caps = [
    a.kind === 'music' ? 'Muziek' : 'Video',
    a.catalogs.length ? `${a.catalogs.length} catalogi` : null,
    a.resources.includes('stream') ? 'Streams' : null,
    a.resources.includes('meta') ? 'Info' : null,
    a.resources.includes('search') || a.catalogs.some((c) => c.searchOnly) ? 'Zoeken' : null,
    a.resources.includes('subtitles') ? 'Ondertitels' : null,
  ].filter(Boolean) as string[];
  return <div className="chip-wrap ad-caps">{caps.map((c) => <span key={c} className="chip chip-static">{c}</span>)}</div>;
}

/** Addons van dit profiel: toevoegen met een manifest-adres of uit een addon-winkel, aan/uit en volgorde. */
export function AddonSettings() {
  const state = useAddons();
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState<ManifestInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (!state) return <p className="placeholder-row">Laden…</p>;
  if (!(state.manage ?? state.allowed)) {
    return <p className="muted settings-intro">Addons staan uit voor dit profiel. De beheerder van de server kan ze aanzetten bij Gebruikers &amp; rechten.</p>;
  }

  const check = async () => {
    setBusy(true);
    setMsg(null);
    setPreview(null);
    try {
      const r = await addonsApi.check(url);
      setPreview(r.addon);
      if (r.addon.registryURL) setMsg('Dit is een addon-winkel. Gebruik hieronder "Uit een addon-winkel".');
    } catch (err) {
      setMsg(errText(err, 'Controleren mislukt.'));
    } finally {
      setBusy(false);
    }
  };

  const add = async (u = url) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await addonsApi.add(u);
      setMsg(`✓ ${r.addon.name} toegevoegd.`);
      setUrl('');
      setPreview(null);
      refreshAddons();
    } catch (err) {
      setMsg(errText(err, 'Toevoegen mislukt.'));
    } finally {
      setBusy(false);
    }
  };

  const list = state.addons;
  const move = async (a: Addon, dir: -1 | 1) => {
    const i = list.findIndex((x) => x.id === a.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const order = [...list];
    [order[i], order[j]] = [order[j], order[i]];
    await Promise.all(order.map((x, n) => (x.order !== n ? addonsApi.patch(x.id, { order: n }) : null)));
    refreshAddons();
  };

  return (
    <>
      <p className="muted settings-intro">
        Addons werken met het open Stremio-protocol (video) en het Eclipse-protocol (muziek). Ze zijn alleen van dit profiel:
        andere profielen en gebruikers zien ze niet. tumedia speelt alleen gewone http(s)-streams af, geen torrents.
      </p>

      <form className="settings-block form-grid" onSubmit={(e) => { e.preventDefault(); void check(); }}>
        <h2>Addon toevoegen</h2>
        <label>Addon-adres (manifest.json)
          <input className="text-input" required value={url} onChange={(e) => { setUrl(e.target.value); setPreview(null); }} placeholder="https://…/manifest.json" inputMode="url" autoComplete="off" spellCheck={false} />
        </label>
        <div className="form-actions">
          <button className="btn" type="submit" disabled={busy || !url.trim()}>{busy && !preview ? 'Controleren…' : 'Controleren'}</button>
        </div>
        {preview && !preview.registryURL && (
          <div className="ad-preview">
            <div className="ad-row-head">
              {preview.icon ? <img className="ad-icon" src={preview.icon} alt="" /> : <span className="ad-icon ad-icon-empty" aria-hidden="true">◇</span>}
              <div>
                <strong>{preview.name}</strong> <span className="subtle">· v{preview.version}</span>
                {preview.description && <p className="subtle ad-desc">{preview.description}</p>}
              </div>
            </div>
            <Caps a={preview} />
            {preview.configurationRequired && <p className="subtle hint">Deze addon moet eerst worden ingesteld op de site van de maker. Plak daarna het adres dat je daar krijgt.</p>}
            <div className="form-actions">
              <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void add()}>Toevoegen</button>
            </div>
          </div>
        )}
        {msg && <p className="subtle" role="status">{msg}</p>}
        <p className="subtle hint">Het adres (met je persoonlijke token) wordt versleuteld op de server bewaard en is daarna niet meer zichtbaar. Alleen de server praat met de addon.</p>
      </form>

      <section className="settings-block" aria-label="Geïnstalleerde addons">
        <h2>Geïnstalleerd</h2>
        {!list.length && <p className="subtle">Nog geen addons.</p>}
        <ul className="ad-list">
          {list.map((a, i) => <AddonRow key={a.id} a={a} first={i === 0} last={i === list.length - 1} onMove={(d) => void move(a, d)} />)}
        </ul>
      </section>

      <StoreBrowser onAdd={(u) => void add(u)} busy={busy} />
    </>
  );
}

function AddonRow({ a, first, last, onMove }: { a: Addon; first: boolean; last: boolean; onMove: (dir: -1 | 1) => void }) {
  const [confirm, setConfirm] = useState(false);
  const patch = async (b: { widgets?: boolean; streams?: boolean }) => {
    await addonsApi.patch(a.id, b).catch(() => undefined);
    refreshAddons();
  };
  return (
    <li className="ad-item">
      <div className="ad-row-head">
        {a.icon ? <img className="ad-icon" src={a.icon} alt="" /> : <span className="ad-icon ad-icon-empty" aria-hidden="true">◇</span>}
        <div>
          <strong>{a.name}</strong> <span className="subtle">· v{a.version}</span>
          <Caps a={a} />
        </div>
        <div className="ad-move">
          <button className="chip" disabled={first} onClick={() => onMove(-1)} aria-label={`${a.name} omhoog`}>↑</button>
          <button className="chip" disabled={last} onClick={() => onMove(1)} aria-label={`${a.name} omlaag`}>↓</button>
        </div>
      </div>
      {a.kind === 'video' && (
        <div className="ad-toggles">
          {a.catalogs.length > 0 && (
            <label className="studio-toggle"><span>Catalogi als widgets</span><span className="toggle"><input type="checkbox" checked={a.widgets} onChange={(e) => void patch({ widgets: e.target.checked })} /></span></label>
          )}
          {a.resources.includes('stream') && (
            <label className="studio-toggle"><span>Als afspeelbron bij titels en wedstrijden</span><span className="toggle"><input type="checkbox" checked={a.streams} onChange={(e) => void patch({ streams: e.target.checked })} /></span></label>
          )}
        </div>
      )}
      <div className="form-actions">
        {confirm ? (
          <>
            <span className="error">{a.name} verwijderen?</span>
            <button className="btn btn-danger btn-sm" onClick={async () => { await addonsApi.remove(a.id).catch(() => undefined); refreshAddons(); }}>Ja, verwijderen</button>
            <button className="btn btn-sm" onClick={() => setConfirm(false)}>Annuleren</button>
          </>
        ) : (
          <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(true)}>Verwijderen</button>
        )}
      </div>
    </li>
  );
}

function StoreBrowser({ onAdd, busy }: { onAdd: (url: string) => void; busy: boolean }) {
  const [url, setUrl] = useState('');
  const [store, setStore] = useState<{ name: string; addons: StoreEntry[] } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const open = async () => {
    setLoading(true);
    setMsg(null);
    try {
      setStore(await addonsApi.store(url));
    } catch (err) {
      setMsg(errText(err, 'Winkel openen mislukt.'));
    } finally {
      setLoading(false);
    }
  };

  const shown = store?.addons.filter((x) => !filter || `${x.name} ${x.description ?? ''}`.toLowerCase().includes(filter.toLowerCase())) ?? [];

  return (
    <section className="settings-block form-grid" aria-label="Uit een addon-winkel">
      <h2>Uit een addon-winkel</h2>
      <p className="subtle hint">Plak het adres van een winkel-manifest (een lijst met addons) en kies er daarna één. Veel addons stel je eerst in op de site van de maker; die opent in een nieuw tabblad.</p>
      <form className="inline-form" onSubmit={(e) => { e.preventDefault(); void open(); }}>
        <input className="text-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/manifest.json" inputMode="url" autoComplete="off" spellCheck={false} aria-label="Adres van de addon-winkel" />
        <button className="btn" type="submit" disabled={loading || !url.trim()}>{loading ? 'Openen…' : 'Openen'}</button>
      </form>
      {msg && <p className="subtle" role="status">{msg}</p>}
      {store && (
        <>
          <div className="account-head">
            <h3>{store.name}</h3>
            <span className="subtle">{store.addons.length} addons</span>
            <input className="text-input text-input-sm" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter" aria-label="Filter addons" />
          </div>
          <ul className="ad-store">
            {shown.map((x) => (
              <li key={x.id} className="ad-store-item">
                {x.icon ? <img className="ad-icon" src={x.icon} alt="" /> : <span className="ad-icon ad-icon-empty" aria-hidden="true">◇</span>}
                <div className="ad-store-body">
                  <strong>{x.name}</strong>
                  {x.author && <span className="subtle"> · {x.author}</span>}
                  {x.description && <p className="subtle ad-desc">{x.description}</p>}
                </div>
                {x.manifestUrl ? (
                  <button className="btn btn-sm" disabled={busy} onClick={() => onAdd(x.manifestUrl!)}>Toevoegen</button>
                ) : x.setupUrl ? (
                  <a className="btn btn-sm" href={x.setupUrl} target="_blank" rel="noreferrer noopener">Instellen ↗</a>
                ) : null}
              </li>
            ))}
          </ul>
          <p className="subtle hint">Na het instellen krijg je een manifest-adres: plak dat bovenaan bij "Addon toevoegen".</p>
        </>
      )}
    </section>
  );
}

/** Woorden waarmee een bron nooit getoond wordt (zoals Xperience's never-show list). */
function NeverList() {
  const prefs = usePrefs();
  const [text, setText] = useState(prefs.sourceNever.join(', '));
  useEffect(() => setText(prefs.sourceNever.join(', ')), [prefs.sourceNever]);
  const save = () => {
    const list = [...new Set(text.split(/[,\n]/).map((x) => x.trim()).filter((x) => x.length >= 2))].slice(0, 30);
    void savePrefs({ sourceNever: list });
  };
  return (
    <label className="ua2-select">Nooit tonen (woorden)
      <input className="text-input" value={text} placeholder="bijv. CAM, HDTS, 3D, HC" onChange={(e) => setText(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
    </label>
  );
}
